
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Edit2,
  Loader2,
  Plus,
  Power,
  RefreshCw,
  Search,
} from 'lucide-react';
import { useSchool } from '../../../context/SchoolContext';
import ConfirmDialog from '../components/ConfirmDialog';
import SettingsModal from '../components/SettingsModal';
import SettingsPageHeader from '../components/SettingsPageHeader';
import StatusBadge from '../components/StatusBadge';
import ToastMessage from '../components/ToastMessage';
import { useToast } from '../hooks/useToast';
import { academicSettingsApi } from '../services/academicSettingsApi';
import { getApiErrorMessage } from '../services/settingsResponse';

const RESOURCE_CONFIG = {
  academicYears: {
    label: 'Academic Years',
    singular: 'Academic Year',
    searchFields: ['year_name'],
  },
  classes: {
    label: 'Classes',
    singular: 'Class',
    searchFields: ['class_name', 'class_code', 'academic_year_name'],
  },
  sections: {
    label: 'Sections',
    singular: 'Section',
    searchFields: ['section_name', 'section_code', 'class_level_name'],
  },
  subjects: {
    label: 'Subjects',
    singular: 'Subject',
    searchFields: ['subject_name', 'subject_code', 'class_level_name'],
  },
};

const inputClass =
  'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-gray-100 disabled:text-gray-500';

const initialFormFor = (resource, record = null) => {
  if (resource === 'academicYears') {
    return {
      year_name: record?.year_name || '',
      start_date: record?.start_date || '',
      end_date: record?.end_date || '',
      is_current: Boolean(record?.is_current),
    };
  }

  if (resource === 'classes') {
    return {
      academic_year_id: record?.academic_year_id || '',
      class_name: record?.class_name || '',
      class_code: record?.class_code || '',
      sort_order: record?.sort_order ?? 0,
      sections: '',
    };
  }

  if (resource === 'sections') {
    return {
      class_level_id: record?.class_level_id || '',
      section_name: record?.section_name || '',
      section_code: record?.section_code || '',
    };
  }

  return {
    class_level_id: record?.class_level_id || '',
    subject_name: record?.subject_name || '',
    subject_code: record?.subject_code || '',
    subject_type: record?.subject_type || 'SCHOLASTIC',
  };
};

const FormField = ({ label, required, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-gray-700">
      {label}
      {required && <span className="ml-1 text-red-500">*</span>}
    </span>
    {children}
  </label>
);

const AcademicRecordForm = ({ resource, form, setForm, editing, data }) => {
  const setValue = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  if (resource === 'academicYears') {
    return (
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div className="md:col-span-2">
          <FormField label="Year name" required>
            <input
              required
              placeholder="2026-2027"
              value={form.year_name}
              onChange={(event) => setValue('year_name', event.target.value)}
              className={inputClass}
            />
          </FormField>
        </div>
        <FormField label="Start date" required>
          <input
            required
            type="date"
            value={form.start_date}
            onChange={(event) => setValue('start_date', event.target.value)}
            className={inputClass}
          />
        </FormField>
        <FormField label="End date" required>
          <input
            required
            type="date"
            value={form.end_date}
            onChange={(event) => setValue('end_date', event.target.value)}
            className={inputClass}
          />
        </FormField>
        <label className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 md:col-span-2">
          <input
            type="checkbox"
            checked={form.is_current}
            onChange={(event) => setValue('is_current', event.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">
            Set as the current academic year
          </span>
        </label>
      </div>
    );
  }

  if (resource === 'classes') {
    return (
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div className="md:col-span-2">
          <FormField label="Academic year" required>
            <select
              required
              disabled={editing}
              value={form.academic_year_id}
              onChange={(event) => setValue('academic_year_id', event.target.value)}
              className={inputClass}
            >
              <option value="">Select academic year</option>
              {data.academicYears.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.year_name}
                </option>
              ))}
            </select>
          </FormField>
          {editing && (
            <p className="mt-1 text-xs text-gray-500">
              The existing backend does not allow changing a class academic year.
            </p>
          )}
        </div>
        <FormField label="Class name" required>
          <input
            required
            placeholder="Grade 1"
            value={form.class_name}
            onChange={(event) => setValue('class_name', event.target.value)}
            className={inputClass}
          />
        </FormField>
        <FormField label="Class code" required>
          <input
            required
            placeholder="GRADE-1"
            value={form.class_code}
            onChange={(event) => setValue('class_code', event.target.value)}
            className={inputClass}
          />
        </FormField>
        <FormField label="Sort order">
          <input
            type="number"
            min="0"
            value={form.sort_order}
            onChange={(event) => setValue('sort_order', Number(event.target.value))}
            className={inputClass}
          />
        </FormField>
        {!editing && (
          <FormField label="Create Sections (optional)">
            <input
              placeholder="e.g. A, B, C"
              value={form.sections || ''}
              onChange={(event) => setValue('sections', event.target.value)}
              className={inputClass}
            />
            <p className="mt-1 text-xs text-gray-500">
              Enter comma-separated names to auto-create sections.
            </p>
          </FormField>
        )}
      </div>
    );
  }

  if (resource === 'sections') {
    return (
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div className="md:col-span-2">
          <FormField label="Class" required>
            <select
              required
              disabled={editing}
              value={form.class_level_id}
              onChange={(event) => setValue('class_level_id', event.target.value)}
              className={inputClass}
            >
              <option value="">Select class</option>
              {data.classes.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.class_name} ({item.class_code})
                </option>
              ))}
            </select>
          </FormField>
          {editing && (
            <p className="mt-1 text-xs text-gray-500">
              The existing backend does not allow changing a section class.
            </p>
          )}
        </div>
        <FormField label="Section name" required>
          <input
            required
            placeholder="Section A"
            value={form.section_name}
            onChange={(event) => setValue('section_name', event.target.value)}
            className={inputClass}
          />
        </FormField>
        <FormField label="Section code" required>
          <input
            required
            placeholder="A"
            value={form.section_code}
            onChange={(event) => setValue('section_code', event.target.value)}
            className={inputClass}
          />
        </FormField>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
      <div className="md:col-span-2">
        <FormField label="Class" required>
          <select
            required
            disabled={editing}
            value={form.class_level_id}
            onChange={(event) => setValue('class_level_id', event.target.value)}
            className={inputClass}
          >
            <option value="">Select class</option>
            {data.classes.map((item) => (
              <option key={item.id} value={item.id}>
                {item.class_name} ({item.class_code})
              </option>
            ))}
          </select>
        </FormField>
        {editing && (
          <p className="mt-1 text-xs text-gray-500">
            The existing backend does not allow changing a subject class.
          </p>
        )}
      </div>
      <FormField label="Subject name" required>
        <input
          required
          placeholder="Mathematics"
          value={form.subject_name}
          onChange={(event) => setValue('subject_name', event.target.value)}
          className={inputClass}
        />
      </FormField>
      <FormField label="Subject code" required>
        <input
          required
          placeholder="MATH"
          value={form.subject_code}
          onChange={(event) => setValue('subject_code', event.target.value)}
          className={inputClass}
        />
      </FormField>
      <FormField label="Subject type" required>
        <select
          value={form.subject_type}
          onChange={(event) => setValue('subject_type', event.target.value)}
          className={inputClass}
        >
          <option value="SCHOLASTIC">Scholastic</option>
          <option value="CO_SCHOLASTIC">Co-Scholastic</option>
        </select>
      </FormField>
    </div>
  );
};

const AcademicTable = ({ resource, rows, onEdit, onToggle }) => {
  if (rows.length === 0) {
    return (
      <div className="py-16 text-center">
        <p className="text-sm font-medium text-gray-700">No records found.</p>
        <p className="mt-1 text-xs text-gray-500">Add a record to get started.</p>
      </div>
    );
  }

  const nameCell = (row) => {
    if (resource === 'academicYears') return row.year_name;
    if (resource === 'classes') return row.class_name;
    if (resource === 'sections') return row.section_name;
    return row.subject_name;
  };

  const codeCell = (row) => {
    if (resource === 'academicYears') return `${row.start_date} to ${row.end_date}`;
    if (resource === 'classes') return row.class_code;
    if (resource === 'sections') return row.section_code;
    return row.subject_code;
  };

  const parentCell = (row) => {
    if (resource === 'academicYears') return row.is_current ? 'Current year' : '—';
    if (resource === 'classes') return row.academic_year_name || '—';
    return row.class_level_name || '—';
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-left">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <th className="px-4 py-3 font-semibold">Name</th>
            <th className="px-4 py-3 font-semibold">
              {resource === 'academicYears' ? 'Period' : 'Code'}
            </th>
            <th className="px-4 py-3 font-semibold">
              {resource === 'academicYears' ? 'Current' : resource === 'classes' ? 'Academic year' : 'Class'}
            </th>
            {resource === 'subjects' && (
              <th className="px-4 py-3 font-semibold">Type</th>
            )}
            <th className="px-4 py-3 font-semibold">Status</th>
            <th className="px-4 py-3 text-right font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => {
            const active = String(row.status || 'ACTIVE').toUpperCase() === 'ACTIVE';
            return (
              <tr key={row.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                  {nameCell(row)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{codeCell(row)}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{parentCell(row)}</td>
                {resource === 'subjects' && (
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {row.subject_type === 'CO_SCHOLASTIC' ? 'Co-Scholastic' : 'Scholastic'}
                  </td>
                )}
                <td className="px-4 py-3">
                  <StatusBadge status={row.status || 'ACTIVE'} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-3 pr-1">
                    <button
                      type="button"
                      onClick={() => onEdit(row)}
                      className="rounded-lg p-1.5 text-gray-500 hover:bg-blue-50 hover:text-blue-700"
                      title="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={active}
                      onClick={() => onToggle(row)}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        active ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                      title={active ? 'Deactivate' : 'Activate'}
                    >
                      <span className="sr-only">Toggle status</span>
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          active ? 'translate-x-4' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const AcademicSettingsPage = () => {
  const { schoolId, refreshAcademicYears } = useSchool();
  const [activeResource, setActiveResource] = useState('academicYears');
  const [data, setData] = useState({
    academicYears: [],
    classes: [],
    sections: [],
    subjects: [],
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState({ open: false, record: null });
  const [form, setForm] = useState(initialFormFor('academicYears'));
  const [saving, setSaving] = useState(false);
  const [confirmRecord, setConfirmRecord] = useState(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const { toast, showToast, clearToast } = useToast();

  const loadAll = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const [academicYears, classes, sections, subjects] = await Promise.all([
        academicSettingsApi.list(schoolId, 'academicYears'),
        academicSettingsApi.list(schoolId, 'classes'),
        academicSettingsApi.list(schoolId, 'sections'),
        academicSettingsApi.list(schoolId, 'subjects'),
      ]);
      setData({ academicYears, classes, sections, subjects });
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to load academic settings.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [schoolId, showToast]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    setSearch('');
  }, [activeResource]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return data[activeResource];
    const fields = RESOURCE_CONFIG[activeResource].searchFields;
    return data[activeResource].filter((row) =>
      fields.some((field) => String(row[field] || '').toLowerCase().includes(query)),
    );
  }, [activeResource, data, search]);

  const openCreate = () => {
    setForm(initialFormFor(activeResource));
    setModal({ open: true, record: null });
  };

  const openEdit = (record) => {
    setForm(initialFormFor(activeResource, record));
    setModal({ open: true, record });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      let saved;
      if (modal.record) {
        const payload = { ...form };
        if (activeResource === 'classes') {
          delete payload.academic_year_id;
          delete payload.sections;
        }
        if (activeResource === 'sections' || activeResource === 'subjects') {
          delete payload.class_level_id;
        }
        saved = await academicSettingsApi.update(
          schoolId,
          activeResource,
          modal.record.id,
          payload,
        );
      } else {
        const payload = { ...form };
        const sectionsToCreate = payload.sections;
        if (activeResource === 'classes') delete payload.sections;

        saved = await academicSettingsApi.create(schoolId, activeResource, payload);

        if (activeResource === 'classes' && sectionsToCreate) {
          const sectionNames = sectionsToCreate.split(',').map((s) => s.trim()).filter(Boolean);
          for (const sName of sectionNames) {
            try {
              await academicSettingsApi.create(schoolId, 'sections', {
                class_level_id: saved.id,
                section_name: sName,
                section_code: `${payload.class_code}-${sName}`.toUpperCase(),
              });
            } catch (err) {
              console.error(`Failed to create section ${sName}`, err);
            }
          }
        }
      }

      setData((current) => {
        const rows = current[activeResource];
        const nextRows = modal.record
          ? rows.map((row) => (row.id === saved.id ? saved : row))
          : [...rows, saved];
        return { ...current, [activeResource]: nextRows };
      });
      setModal({ open: false, record: null });
      showToast(
        `${RESOURCE_CONFIG[activeResource].singular} ${modal.record ? 'updated' : 'created'} successfully.`,
      );
      await loadAll();
      if (activeResource === 'academicYears') {
        await refreshAcademicYears({ preserveSelection: true });
      }
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to save the record.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async () => {
    if (!confirmRecord) return;
    const nextStatus =
      String(confirmRecord.status || 'ACTIVE').toUpperCase() === 'ACTIVE'
        ? 'INACTIVE'
        : 'ACTIVE';

    setStatusSaving(true);
    try {
      const updated = await academicSettingsApi.updateStatus(
        schoolId,
        activeResource,
        confirmRecord.id,
        nextStatus,
      );
      setData((current) => ({
        ...current,
        [activeResource]: current[activeResource].map((row) =>
          row.id === updated.id ? updated : row,
        ),
      }));
      setConfirmRecord(null);
      showToast(
        `${RESOURCE_CONFIG[activeResource].singular} ${
          nextStatus === 'ACTIVE' ? 'activated' : 'deactivated'
        } successfully.`,
      );
      if (activeResource === 'academicYears') {
        await refreshAcademicYears({ preserveSelection: true });
      }
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to update status.'), 'error');
    } finally {
      setStatusSaving(false);
    }
  };

  const currentActive =
    String(confirmRecord?.status || 'ACTIVE').toUpperCase() === 'ACTIVE';

  return (
    <>
      <ToastMessage toast={toast} onClose={clearToast} />
      <SettingsPageHeader
        title="Academic Setup"
        description="Manage academic years, classes, sections and subjects using the existing backend APIs."
        action={
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add {RESOURCE_CONFIG[activeResource].singular}
          </button>
        }
      />

      <div className="mb-5 flex flex-wrap gap-2">
        {Object.entries(RESOURCE_CONFIG).map(([key, config]) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveResource(key)}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
              activeResource === key
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {config.label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={`Search ${RESOURCE_CONFIG[activeResource].label.toLowerCase()}...`}
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <button
            type="button"
            onClick={loadAll}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="flex min-h-[280px] items-center justify-center text-sm text-gray-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading academic data...
          </div>
        ) : (
          <AcademicTable
            resource={activeResource}
            rows={filteredRows}
            onEdit={openEdit}
            onToggle={setConfirmRecord}
          />
        )}
      </div>

      <SettingsModal
        open={modal.open}
        title={`${modal.record ? 'Edit' : 'Add'} ${RESOURCE_CONFIG[activeResource].singular}`}
        description="Fields shown here match the existing backend serializer."
        onClose={() => !saving && setModal({ open: false, record: null })}
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setModal({ open: false, record: null })}
              disabled={saving}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="academic-record-form"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {modal.record ? 'Save changes' : 'Create'}
            </button>
          </div>
        }
      >
        <form id="academic-record-form" onSubmit={handleSave}>
          <AcademicRecordForm
            resource={activeResource}
            form={form}
            setForm={setForm}
            editing={Boolean(modal.record)}
            data={data}
          />
        </form>
      </SettingsModal>

      <ConfirmDialog
        open={Boolean(confirmRecord)}
        title={`${currentActive ? 'Deactivate' : 'Activate'} ${
          RESOURCE_CONFIG[activeResource].singular
        }`}
        message={`Are you sure you want to ${currentActive ? 'deactivate' : 'activate'} this record? Existing related records are not deleted.`}
        confirmLabel={currentActive ? 'Deactivate' : 'Activate'}
        danger={currentActive}
        loading={statusSaving}
        onCancel={() => setConfirmRecord(null)}
        onConfirm={handleStatusChange}
      />
    </>
  );
};

export default AcademicSettingsPage;
